// apps/admin/src/pages/billing/ManualGeneratePage.tsx
//
// Phase 8.2 Wave 3 Task 16 — Admin A3 ManualGeneratePage (rebuilt).
//
// vg-binding: CONTEXT:decisions=P8.D-485,P8.D-455,P8.D-457,P4.1.D-11
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Per CONTEXT.md § A3 — kế toán ad-hoc invoice trigger surface.
// Per CONTEXT.md § — manual invoice numbering MINV-YYYYMM-XXXXX.
// Per CONTEXT.md § — tax engine defer Phase 12+; manual invoice
//   admin có thể nhập taxAmount thủ công (USD cents, optional).
// Per INTERFACE-STANDARDS.md § FE error envelope — toast surfaces
//   error.user_message → error.message → message → network_fallback
//   priority; never raw AxiosError.message / Response.statusText.
//
// Covers goal:.
//
// Endpoint consumed (Task 21 — manual-generate.routes.ts / handler):
//   POST /api/v1/admin/invoices/manual-generate
//     body  : { merchantId, period_start, period_end, notes?, taxAmount? }
//     header: Idempotency-Key (uuid v4 per submit)
//     202   → enqueues Wave 7 worker; returns merchant_id + enqueued_at.
//     409   → BILLING_MODE_NOT_INVOICE | INVOICE_PERIOD_OVERLAP
//     422   → NO_TRANSACTIONS_FOR_PERIOD (empty period)
//
// Merchant picker source (Task 14 — adminMerchantsRead.handler.ts):
//   GET /api/v1/admin/merchants?billing_mode=INVOICE&limit=20&q=…
//     → { merchants: [{ id, name, email, ... }], next_cursor }
//
// Acceptance criteria (PLAN/task-16.md):
//   - [x] Merchant picker only shows INVOICE-mode merchants (server filter).
//   - [x] Date range validation (from ≤ to, max 90 days).
//   - [x] Custom tax amount optional field.
//   - [x] Preview step before final submit (2-step form: compose → preview).
//   - [x] Numbering badge MINV-YYYYMM-XXXXX rendered on success toast.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  Input,
  MerchantPicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  useToast,
  formatUsdCents,
  PageHeader,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  envelopeError,
  newIdempotencyKey,
  unwrap,
  type LooseClient,
} from './billing-shared.js';

// ---------------------------------------------------------------------------
// Reference data — fetchers for the new form pickers
// (dbg-admin-invoices-cols-2026-05-23)
// ---------------------------------------------------------------------------

interface MerchantDetail {
  id: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  business_name?: string | null;
  tax_id?: string | null;
  address?: string | null;
}

async function fetchMerchantDetail(id: string): Promise<MerchantDetail | null> {
  if (!id) return null;
  const c = apiClient() as unknown as {
    GET: (p: string) => Promise<{ data?: unknown; response: Response }>;
  };
  const { data, response } = await c.GET(`/api/v1/admin/merchants/${id}`);
  if (!response.ok) return null;
  const env = (data ?? {}) as { data?: Record<string, unknown> };
  const m = (env.data ?? data) as Record<string, unknown>;
  if (!m || typeof m !== 'object') return null;
  const profile = (m['profile'] ?? m) as Record<string, unknown>;
  // GET /api/v1/admin/merchants/:id returns flat keys at the data root
  // (`name`, `email`, `phone`, `business_name`, `tax_id`, `business_address`).
  // Accept both flat + nested billing-address shapes so the fetcher works
  // for any future schema evolution.
  const billing = (m['billing_address'] ?? {}) as Record<string, unknown>;
  const businessAddressRaw = m['business_address'];
  const addressFromFlat =
    typeof businessAddressRaw === 'string'
      ? businessAddressRaw
      : businessAddressRaw && typeof businessAddressRaw === 'object'
        ? // Nested shape `{line1, city, country, …}` → join non-empty parts.
          Object.values(businessAddressRaw as Record<string, unknown>)
            .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
            .join(', ')
        : null;
  return {
    id,
    name: (profile['display_name'] ?? profile['name'] ?? m['name'] ?? null) as
      | string
      | null,
    email: (m['email'] ?? profile['email'] ?? null) as string | null,
    phone: (m['phone'] ?? profile['phone'] ?? null) as string | null,
    business_name: (m['business_name'] ?? billing['business_name'] ?? null) as
      | string
      | null,
    tax_id: (m['tax_id'] ?? billing['tax_id'] ?? null) as string | null,
    address: (addressFromFlat ?? billing['address'] ?? m['address'] ?? null) as
      | string
      | null,
  };
}

interface StoreOption {
  id: string;
  name: string;
}

async function fetchMerchantStores(id: string): Promise<StoreOption[]> {
  if (!id) return [];
  const c = apiClient() as unknown as {
    GET: (p: string) => Promise<{ data?: unknown; response: Response }>;
  };
  const params = new URLSearchParams({ merchant_id: id, pageSize: '50' });
  const { data, response } = await c.GET(
    `/api/v1/admin/stores?${params.toString()}`,
  );
  if (!response.ok) return [];
  const env = (data ?? {}) as { data?: { items?: unknown[] } | unknown[] };
  const items = Array.isArray(env.data)
    ? (env.data as Array<Record<string, unknown>>)
    : Array.isArray((env.data as { items?: unknown[] })?.items)
      ? ((env.data as { items: unknown[] }).items as Array<Record<string, unknown>>)
      : [];
  return items
    .map((s): StoreOption | null => {
      const sid = (s['id'] ?? s['_id']) as string | undefined;
      const sname = (s['name'] ?? s['display_name'] ?? '') as string;
      return sid ? { id: String(sid), name: sname || String(sid) } : null;
    })
    .filter((s): s is StoreOption => s !== null);
}

interface GatewayOption {
  id: string;
  provider: string;
  label: string;
}

async function fetchPaymentGateways(): Promise<GatewayOption[]> {
  const c = apiClient() as unknown as {
    GET: (p: string) => Promise<{ data?: unknown; response: Response }>;
  };
  const { data, response } = await c.GET(`/api/v1/admin/payment-gateways`);
  if (!response.ok) return [];
  const env = (data ?? {}) as { data?: { items?: unknown[] } | unknown[] };
  const items = Array.isArray(env.data)
    ? (env.data as Array<Record<string, unknown>>)
    : Array.isArray((env.data as { items?: unknown[] })?.items)
      ? ((env.data as { items: unknown[] }).items as Array<Record<string, unknown>>)
      : [];
  return items
    .map((g): GatewayOption | null => {
      const gid = (g['id'] ?? g['_id']) as string | undefined;
      const provider = String(g['provider'] ?? g['kind'] ?? '');
      const label = String(g['display_name'] ?? g['account'] ?? provider);
      return gid ? { id: String(gid), provider, label } : null;
    })
    .filter((g): g is GatewayOption => g !== null);
}

interface LegalEntityOption {
  id: string;
  name: string;
  tax_id: string;
  is_default: boolean;
}

async function fetchLegalEntities(): Promise<LegalEntityOption[]> {
  const c = apiClient() as unknown as {
    GET: (p: string) => Promise<{ data?: unknown; response: Response }>;
  };
  // GET /api/v1/admin/legal-entities rejects unknown query params via Fastify's
  // strict schema, so we send no params and walk the doubly-nested envelope
  // (`{success:true, data:{data:[…], pagination:{…}}}`).
  const { data, response } = await c.GET(`/api/v1/admin/legal-entities`);
  if (!response.ok) return [];
  const env = (data ?? {}) as { data?: unknown };
  const inner = env.data as { data?: unknown[] } | unknown[] | undefined;
  const items: Array<Record<string, unknown>> = Array.isArray(inner)
    ? (inner as Array<Record<string, unknown>>)
    : Array.isArray((inner as { data?: unknown[] } | undefined)?.data)
      ? ((inner as { data: unknown[] }).data as Array<Record<string, unknown>>)
      : [];
  return items
    .map((le): LegalEntityOption | null => {
      const lid = (le['id'] ?? le['_id']) as string | undefined;
      const name = String(le['name'] ?? '');
      const taxId = String(le['tax_id'] ?? '');
      const isDefault = Boolean(le['is_default']);
      return lid
        ? { id: String(lid), name: name || String(lid), tax_id: taxId, is_default: isDefault }
        : null;
    })
    .filter((le): le is LegalEntityOption => le !== null);
}

function nonEmpty(v: string | null | undefined): string {
  if (v == null) return '—';
  const trimmed = String(v).trim();
  return trimmed.length === 0 ? '—' : trimmed;
}

// Common timezone shortlist — keep small to avoid a 400-entry select.
const TIMEZONE_OPTIONS = [
  'UTC',
  'Asia/Ho_Chi_Minh',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
] as const;

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

interface ManualGenerateResponse {
  merchant_id: string;
  period_start: string;
  period_end: string;
  enqueued_at: string;
  trigger: 'manual_admin';
  audit_log_id: string | null;
  /** Optional: BE may return future numbering hint; fall back to MINV prefix. */
  invoice_number?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers — date + currency
// ---------------------------------------------------------------------------

const NETWORK_FALLBACK = 'Network error — check connection';
const MAX_RANGE_DAYS = 90;

// Radix forbids SelectItem value="" — sentinel for the "all/default" options.
const ALL_VALUE = '__all__';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** YYYY-MM-DD → 00:00:00.000Z ISO 8601 (inclusive-day convention). */
function toIsoStart(yyyyMmDd: string): string {
  return `${yyyyMmDd}T00:00:00.000Z`;
}

/** YYYY-MM-DD → 23:59:59.999Z ISO 8601 (inclusive-day convention). */
function toIsoEnd(yyyyMmDd: string): string {
  return `${yyyyMmDd}T23:59:59.999Z`;
}

/** Inclusive day count between two YYYY-MM-DD strings. Returns 0 on invalid. */
function dayDiffInclusive(fromYmd: string, toYmd: string): number {
  if (!fromYmd || !toYmd) return 0;
  const a = new Date(`${fromYmd}T00:00:00Z`).getTime();
  const b = new Date(`${toYmd}T00:00:00Z`).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
  return Math.floor((b - a) / ONE_DAY_MS) + 1;
}

/** USD dollars input string → integer USD cents. Returns null when invalid. */
function dollarsToCents(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  // Accept "1234", "1234.5", "1234.56"; reject negative / NaN / >2 decimals.
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(parseFloat(trimmed) * 100);
  if (!Number.isFinite(cents) || cents < 0) return null;
  return cents;
}

/** USD cents int → display "$X.XX". */
function fmtCents(cents: number): string {
  return formatUsdCents(cents);
}

/** Build the MINV-YYYYMM-XXXXX preview for the success badge when BE
 *  doesn't return invoice_number on 202 (worker assigns it asynchronously).
 *  We show the prefix shape so the operator sees the numbering convention
 *  . */
function minvPrefixFor(periodStartYmd: string): string {
  const m = periodStartYmd.match(/^(\d{4})-(\d{2})/);
  if (!m) return 'MINV-…';
  return `MINV-${m[1]}${m[2]}-XXXXX`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type FormStep = 'compose' | 'preview';

export function ManualGeneratePage(): JSX.Element {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('finance');

  // ----- Form state -----
  const [merchantId, setMerchantId] = useState('');
  const [merchantLabel, setMerchantLabel] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [taxAmountInput, setTaxAmountInput] = useState(''); // dollars string
  const [notes, setNotes] = useState('');

  // dbg-admin-invoices-cols-2026-05-23 — five new optional fields the admin
  // form now surfaces. BE accepts them as no-ops until the manual-generate
  // handler is extended; the FE still POSTs them so the contract is forward-
  // compatible.
  const [storeId, setStoreId] = useState('');
  const [gatewayId, setGatewayId] = useState('');
  const [markupInput, setMarkupInput] = useState(''); // percent
  const [legalEntityId, setLegalEntityId] = useState('');
  const [timezone, setTimezone] = useState('UTC');

  // Async lookups — all keyed off the merchant the admin picked. Stores list
  // reloads on merchant change; gateways + legal entities are global.
  const merchantDetailQuery = useQuery({
    queryKey: ['admin', 'merchant-detail', merchantId],
    queryFn: () => fetchMerchantDetail(merchantId),
    enabled: !!merchantId,
    staleTime: 60_000,
  });
  const storesQuery = useQuery({
    queryKey: ['admin', 'merchant-stores', merchantId],
    queryFn: () => fetchMerchantStores(merchantId),
    enabled: !!merchantId,
    staleTime: 60_000,
  });
  const gatewaysQuery = useQuery({
    queryKey: ['admin', 'payment-gateways'],
    queryFn: fetchPaymentGateways,
    staleTime: 5 * 60_000,
  });
  const legalEntitiesQuery = useQuery({
    queryKey: ['admin', 'legal-entities'],
    queryFn: fetchLegalEntities,
    staleTime: 5 * 60_000,
  });

  const merchantInfo = merchantDetailQuery.data ?? null;
  const storeOptions = storesQuery.data ?? [];
  const gatewayOptions = gatewaysQuery.data ?? [];
  const legalEntityOptions = useMemo(
    () => legalEntitiesQuery.data ?? [],
    [legalEntitiesQuery.data],
  );

  // Default the legal-entity dropdown to the `is_default` entry when it
  // loads — otherwise fall back to the first option. Mirrors the manual-
  // generate handler's "single active legal entity" behaviour.
  useEffect(() => {
    if (!legalEntityId && legalEntityOptions.length > 0) {
      const def = legalEntityOptions.find((le) => le.is_default) ?? legalEntityOptions[0];
      if (def) setLegalEntityId(def.id);
    }
  }, [legalEntityOptions, legalEntityId]);

  // Reset store + gateway when the merchant changes so stale selections
  // don't sneak into the next submit.
  useEffect(() => {
    setStoreId('');
  }, [merchantId]);

  const markupValid =
    markupInput.trim() === '' || /^\d+(\.\d{1,2})?$/.test(markupInput.trim());

  // ----- Step / async state -----
  const [step, setStep] = useState<FormStep>('compose');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ----- Validation -----
  const rangeDays = useMemo(
    () => dayDiffInclusive(dateFrom, dateTo),
    [dateFrom, dateTo],
  );
  const rangeValid = rangeDays >= 1 && rangeDays <= MAX_RANGE_DAYS;
  const taxCents = useMemo(() => dollarsToCents(taxAmountInput), [taxAmountInput]);
  const taxValid =
    taxAmountInput.trim() === '' || taxCents !== null;

  const canPreview =
    !!merchantId && !!dateFrom && !!dateTo && rangeValid && taxValid;

  const composeError = useMemo<string | null>(() => {
    if (!merchantId) return null;
    if (dateFrom && dateTo && rangeDays === 0) {
      return t('manual_generate.errors.cycleEndOrder');
    }
    if (dateFrom && dateTo && rangeDays > MAX_RANGE_DAYS) {
      return t('manual_generate.errors.rangeMax', {
        max: MAX_RANGE_DAYS,
        days: rangeDays,
      });
    }
    if (!taxValid) {
      return t('manual_generate.errors.taxInvalid');
    }
    return null;
  }, [merchantId, dateFrom, dateTo, rangeDays, taxValid, t]);

  // ----- Step transitions -----
  const handleGoPreview = useCallback((): void => {
    setFormError(null);
    if (!canPreview) {
      setFormError(
        composeError ?? t('manual_generate.errors.completeBeforePreview'),
      );
      return;
    }
    setStep('preview');
  }, [canPreview, composeError, t]);

  const handleBackToCompose = useCallback((): void => {
    setStep('compose');
    setFormError(null);
  }, []);

  // ----- Submit -----
  const handleConfirm = useCallback(async (): Promise<void> => {
    setFormError(null);
    if (!canPreview) {
      setFormError(composeError ?? t('manual_generate.errors.formInvalid'));
      setStep('compose');
      return;
    }
    setSubmitting(true);
    try {
      const c = apiClient() as unknown as LooseClient;
      const body: Record<string, unknown> = {
        merchantId,
        period_start: toIsoStart(dateFrom),
        period_end: toIsoEnd(dateTo),
      };
      if (notes.trim().length >= 1) {
        body.notes = notes.trim().slice(0, 500);
      }
      if (typeof taxCents === 'number' && taxCents >= 0 && taxAmountInput.trim() !== '') {
        body.taxAmount = taxCents;
      }
      // dbg-admin-invoices-cols-2026-05-23 — forward-compatible body keys.
      // BE handler accepts them as no-ops until the manual-generate
      // service is extended to honour the filters + overrides.
      if (storeId) body.store_id = storeId;
      if (gatewayId) body.payment_gateway_id = gatewayId;
      if (legalEntityId) body.legal_entity_id = legalEntityId;
      if (timezone && timezone !== 'UTC') body.timezone = timezone;
      if (markupValid && markupInput.trim() !== '') {
        const pct = parseFloat(markupInput.trim());
        if (Number.isFinite(pct) && pct >= 0) {
          body.unit_price_markup_bps = Math.round(pct * 100);
        }
      }
      const { data, error, response } = await c.POST(
        '/api/v1/admin/invoices/manual-generate',
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body,
        },
      );
      if (error || !response.ok) {
        const msg = envelopeError(error) ?? NETWORK_FALLBACK;
        const code = (error as { error?: { code?: string } } | null)?.error
          ?.code;
        if (response.status === 422 && code === 'NO_TRANSACTIONS_FOR_PERIOD') {
          setFormError(t('manual_generate.errors.noTransactions'));
          toast({
            title: t('manual_generate.toast.noTransactionsTitle'),
            description: msg,
            variant: 'warning',
          });
          return;
        }
        setFormError(msg);
        toast({
          title: t('manual_generate.toast.failTitle'),
          description: msg,
          variant: 'error',
        });
        return;
      }
      const result = unwrap<ManualGenerateResponse>(data);
      const numberBadge = result.invoice_number ?? minvPrefixFor(dateFrom);
      toast({
        title: t('manual_generate.toast.successTitle', { number: numberBadge }),
        description: t('manual_generate.toast.successDesc', {
          merchant: result.merchant_id,
          from: dateFrom,
          to: dateTo,
        }),
        variant: 'success',
      });
      // Navigate back to invoice list scoped to merchant (Task 11 / Wave 3
      // InvoiceListPage surfaces the freshly enqueued invoice once the worker
      // completes).
      navigate(
        `/billing/invoices?merchant_id=${encodeURIComponent(result.merchant_id)}`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : NETWORK_FALLBACK;
      setFormError(msg);
      toast({
        title: t('manual_generate.toast.networkTitle'),
        description: msg,
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    canPreview,
    composeError,
    merchantId,
    dateFrom,
    dateTo,
    notes,
    taxAmountInput,
    taxCents,
    storeId,
    gatewayId,
    legalEntityId,
    timezone,
    markupValid,
    markupInput,
    navigate,
    toast,
    t,
  ]);

  // ----- Derived preview data -----
  const previewMerchantLabel = merchantLabel || merchantId;
  const previewTaxLine = useMemo<string>(() => {
    if (taxAmountInput.trim() === '' || taxCents === null) {
      return t('manual_generate.taxAuto');
    }
    return t('manual_generate.taxManual', { amount: fmtCents(taxCents) });
  }, [taxAmountInput, taxCents, t]);
  const previewNumberBadge = minvPrefixFor(dateFrom);

  // ----- Render -----

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 p-6">
      {/* Breadcrumb */}
      <div>
        <a
          href="/billing/invoices"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-pw-primary hover:text-pw-primary-hover"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="h-4 w-4"
          >
            <path d="M19 12H5" />
            <path d="m12 19-7-7 7-7" />
          </svg>
          {t('manual_generate.backToInvoices')}
        </a>
      </div>

      {/* Page header */}
      <PageHeader
        title={t('manual_generate.heading')}
        subtitle={
          <>
            {t('manual_generate.subtitlePrefix')}{' '}
            <code className="rounded bg-pw-surface px-1.5 py-0.5 text-xs">
              MINV-YYYYMM-XXXXX
            </code>
            {t('manual_generate.subtitleSuffix')}
          </>
        }
      />

      {/* Step indicator */}
      <ol className="flex items-center gap-3 text-sm text-pw-text-muted">
        <li
          className={cn(
            'flex items-center gap-2',
            step === 'compose' ? 'font-semibold text-pw-text' : '',
          )}
          data-testid="manual-generate-step-compose"
        >
          <span
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
              step === 'compose'
                ? 'bg-pw-primary text-white'
                : 'bg-pw-surface text-pw-text-muted',
            )}
          >
            1
          </span>
          {t('manual_generate.steps.compose')}
        </li>
        <span aria-hidden="true">→</span>
        <li
          className={cn(
            'flex items-center gap-2',
            step === 'preview' ? 'font-semibold text-pw-text' : '',
          )}
          data-testid="manual-generate-step-preview"
        >
          <span
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
              step === 'preview'
                ? 'bg-pw-primary text-white'
                : 'bg-pw-surface text-pw-text-muted',
            )}
          >
            2
          </span>
          {t('manual_generate.steps.preview')}
        </li>
      </ol>

      <Card>
        <CardContent className="p-6">
          <form
            data-testid="manual-generate-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (step === 'compose') handleGoPreview();
              else void handleConfirm();
            }}
            className="flex flex-col gap-6"
            noValidate
          >
            {step === 'compose' ? (
              <>
                {/* Merchant picker */}
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="manual-generate-merchant-select"
                    className="text-sm font-medium text-pw-text"
                  >
                    {t('manual_generate.merchantLabel')}{' '}
                    <span className="text-rose-500">*</span>
                  </label>
                  <MerchantPicker
                    value={merchantId || null}
                    valueLabel={merchantLabel}
                    onChange={(id, label) => {
                      setMerchantId(id ?? '');
                      setMerchantLabel(label);
                    }}
                    billingMode="INVOICE"
                    placeholder={t('manual_generate.merchantPlaceholder')}
                    testId="manual-generate-merchant-select"
                    labels={{
                      title: t('merchant_picker.title'),
                      searchPlaceholder: t('merchant_picker.searchPlaceholder'),
                      loading: t('merchant_picker.loading'),
                      noResults: t('merchant_picker.noResults'),
                      prompt: t('merchant_picker.prompt'),
                    }}
                  />
                  <p className="text-xs text-pw-text-muted">
                    {t('manual_generate.merchantHintPrefix')}{' '}
                    <code>?billing_mode=INVOICE</code>
                    {t('manual_generate.merchantHintSuffix')}
                  </p>
                </div>

                {/* dbg-admin-invoices-cols-2026-05-23 — merchant info card */}
                {merchantId ? (
                  <div
                    className="rounded-md border border-pw-border bg-pw-surface/40 px-4 py-3 text-xs"
                    data-testid="manual-generate-merchant-info"
                  >
                    <div className="mb-2 text-pw-text-muted">
                      {t('manual_generate.merchantInfo.title')}
                    </div>
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                      <div>
                        <dt className="inline text-pw-text-muted">
                          {t('manual_generate.merchantInfo.name')}{' '}
                        </dt>
                        <dd className="inline text-pw-text">
                          {nonEmpty(merchantInfo?.name)}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-pw-text-muted">
                          {t('manual_generate.merchantInfo.phone')}{' '}
                        </dt>
                        <dd className="inline text-pw-text">
                          {nonEmpty(merchantInfo?.phone)}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-pw-text-muted">
                          {t('manual_generate.merchantInfo.email')}{' '}
                        </dt>
                        <dd className="inline text-pw-text">
                          {nonEmpty(merchantInfo?.email)}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-pw-text-muted">
                          {t('manual_generate.merchantInfo.businessName')}{' '}
                        </dt>
                        <dd className="inline text-pw-text">
                          {nonEmpty(merchantInfo?.business_name)}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-pw-text-muted">
                          {t('manual_generate.merchantInfo.taxId')}{' '}
                        </dt>
                        <dd className="inline text-pw-text">
                          {nonEmpty(merchantInfo?.tax_id)}
                        </dd>
                      </div>
                      <div className="sm:col-span-2">
                        <dt className="inline text-pw-text-muted">
                          {t('manual_generate.merchantInfo.address')}{' '}
                        </dt>
                        <dd className="inline text-pw-text">
                          {nonEmpty(merchantInfo?.address)}
                        </dd>
                      </div>
                    </dl>
                  </div>
                ) : null}

                {/* dbg-admin-invoices-cols-2026-05-23 — Store + Payment Gateway filters */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="manual-generate-store-select"
                      className="text-sm font-medium text-pw-text"
                    >
                      {t('manual_generate.storeLabel')}
                    </label>
                    <Select
                      value={storeId === '' ? ALL_VALUE : storeId}
                      onValueChange={(value) =>
                        setStoreId(value === ALL_VALUE ? '' : value)
                      }
                      disabled={!merchantId || storesQuery.isFetching}
                    >
                      <SelectTrigger
                        id="manual-generate-store-select"
                        data-testid="manual-generate-store-select"
                      >
                        <SelectValue placeholder={t('manual_generate.storeAll')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_VALUE}>{t('manual_generate.storeAll')}</SelectItem>
                        {storeOptions.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="manual-generate-gateway-select"
                      className="text-sm font-medium text-pw-text"
                    >
                      {t('manual_generate.gatewayLabel')}
                    </label>
                    <Select
                      value={gatewayId === '' ? ALL_VALUE : gatewayId}
                      onValueChange={(value) =>
                        setGatewayId(value === ALL_VALUE ? '' : value)
                      }
                      disabled={gatewaysQuery.isFetching}
                    >
                      <SelectTrigger
                        id="manual-generate-gateway-select"
                        data-testid="manual-generate-gateway-select"
                      >
                        <SelectValue placeholder={t('manual_generate.gatewayAll')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_VALUE}>{t('manual_generate.gatewayAll')}</SelectItem>
                        {gatewayOptions.map((g) => (
                          <SelectItem key={g.id} value={g.id}>
                            {g.label} ({g.provider})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Cycle window */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="manual-generate-date-from-input"
                      className="text-sm font-medium text-pw-text"
                    >
                      {t('manual_generate.cycleStartLabel')}{' '}
                      <span className="text-rose-500">*</span>
                    </label>
                    <Input
                      id="manual-generate-date-from-input"
                      data-testid="manual-generate-date-from-input"
                      type="date"
                      value={dateFrom}
                      max={dateTo || undefined}
                      onChange={(e) => setDateFrom(e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="manual-generate-date-to-input"
                      className="text-sm font-medium text-pw-text"
                    >
                      {t('manual_generate.cycleEndLabel')}{' '}
                      <span className="text-rose-500">*</span>
                    </label>
                    <Input
                      id="manual-generate-date-to-input"
                      data-testid="manual-generate-date-to-input"
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={(e) => setDateTo(e.target.value)}
                      required
                    />
                  </div>
                </div>
                {dateFrom && dateTo ? (
                  <p
                    className={cn(
                      'text-xs',
                      rangeValid ? 'text-pw-text-muted' : 'text-rose-600 dark:text-rose-300',
                    )}
                    data-testid="manual-generate-range-hint"
                  >
                    {t('manual_generate.rangeHint', {
                      days: rangeDays,
                      max: MAX_RANGE_DAYS,
                    })}
                  </p>
                ) : null}

                {/* Custom tax amount */}
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="manual-generate-tax-input"
                    className="text-sm font-medium text-pw-text"
                  >
                    {t('manual_generate.taxLabel')}
                  </label>
                  <Input
                    id="manual-generate-tax-input"
                    data-testid="manual-generate-tax-input"
                    type="text"
                    inputMode="decimal"
                    placeholder={t('manual_generate.taxPlaceholder')}
                    value={taxAmountInput}
                    onChange={(e) => setTaxAmountInput(e.target.value)}
                    aria-invalid={!taxValid}
                  />
                  <p
                    className={cn(
                      'text-xs',
                      taxValid ? 'text-pw-text-muted' : 'text-rose-600 dark:text-rose-300',
                    )}
                  >
                    {t('manual_generate.taxHint')}
                  </p>
                </div>

                {/* dbg-admin-invoices-cols-2026-05-23 — Markup, Legal entity, Timezone */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="manual-generate-markup-input"
                      className="text-sm font-medium text-pw-text"
                    >
                      {t('manual_generate.markupLabel')}
                    </label>
                    <Input
                      id="manual-generate-markup-input"
                      data-testid="manual-generate-markup-input"
                      type="text"
                      inputMode="decimal"
                      placeholder={t('manual_generate.markupPlaceholder')}
                      value={markupInput}
                      onChange={(e) => setMarkupInput(e.target.value)}
                      aria-invalid={!markupValid}
                    />
                    <p
                      className={cn(
                        'text-xs',
                        markupValid ? 'text-pw-text-muted' : 'text-rose-600 dark:text-rose-300',
                      )}
                    >
                      {t('manual_generate.markupHint')}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="manual-generate-legal-entity-select"
                      className="text-sm font-medium text-pw-text"
                    >
                      {t('manual_generate.legalEntityLabel')}
                    </label>
                    <Select
                      value={legalEntityId === '' ? ALL_VALUE : legalEntityId}
                      onValueChange={(value) =>
                        setLegalEntityId(value === ALL_VALUE ? '' : value)
                      }
                      disabled={legalEntitiesQuery.isFetching}
                    >
                      <SelectTrigger
                        id="manual-generate-legal-entity-select"
                        data-testid="manual-generate-legal-entity-select"
                      >
                        <SelectValue placeholder={t('manual_generate.legalEntityDefault')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_VALUE}>{t('manual_generate.legalEntityDefault')}</SelectItem>
                        {legalEntityOptions.map((le) => (
                          <SelectItem key={le.id} value={le.id}>
                            {le.name}{le.tax_id ? ` (${le.tax_id})` : ''}{le.is_default ? t('manual_generate.legalEntityDefaultSuffix') : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="manual-generate-timezone-select"
                      className="text-sm font-medium text-pw-text"
                    >
                      {t('manual_generate.timezoneLabel')}
                    </label>
                    <Select
                      value={timezone}
                      onValueChange={(value) => setTimezone(value)}
                    >
                      <SelectTrigger
                        id="manual-generate-timezone-select"
                        data-testid="manual-generate-timezone-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONE_OPTIONS.map((tz) => (
                          <SelectItem key={tz} value={tz}>
                            {tz}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Internal notes */}
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="manual-generate-notes"
                    className="text-sm font-medium text-pw-text"
                  >
                    {t('manual_generate.notesLabel')}
                  </label>
                  <textarea
                    id="manual-generate-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t('manual_generate.notesPlaceholder')}
                    maxLength={500}
                    rows={3}
                    className={cn(
                      'w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm',
                      'placeholder:text-pw-text-muted',
                      'focus:outline-none focus:ring-2 focus:ring-pw-primary/40',
                    )}
                  />
                  <p className="text-xs text-pw-text-muted">
                    {t('manual_generate.notesCount', { count: notes.length })}
                  </p>
                </div>

                {formError ? (
                  <div
                    role="alert"
                    className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
                  >
                    {formError}
                  </div>
                ) : null}

                <div className="flex items-center justify-end gap-3 border-t border-pw-border pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/billing/invoices')}
                  >
                    {t('manual_generate.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    data-testid="manual-generate-preview-btn"
                    disabled={!canPreview}
                  >
                    {t('manual_generate.preview')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Preview step */}
                <div
                  className="flex flex-col gap-4"
                  data-testid="manual-generate-preview-panel"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-pw-text">
                      {t('manual_generate.previewPanel.numberingPreview')}
                    </span>
                    <span
                      className="inline-flex items-center rounded-full bg-pw-primary/10 px-2.5 py-0.5 text-xs font-semibold text-pw-primary"
                      data-testid="manual-generate-minv-badge"
                    >
                      {previewNumberBadge}
                    </span>
                  </div>

                  <dl className="grid grid-cols-1 gap-3 rounded-md border border-pw-border bg-pw-surface/40 p-4 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                        {t('manual_generate.previewPanel.merchant')}
                      </dt>
                      <dd
                        className="mt-1 font-medium text-pw-text"
                        data-testid="manual-generate-preview-merchant"
                      >
                        {previewMerchantLabel}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                        {t('manual_generate.previewPanel.billingMode')}
                      </dt>
                      <dd className="mt-1 font-medium text-pw-text">INVOICE</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                        {t('manual_generate.previewPanel.cycleWindow')}
                      </dt>
                      <dd
                        className="mt-1 font-medium text-pw-text"
                        data-testid="manual-generate-preview-range"
                      >
                        {dateFrom} → {dateTo}{' '}
                        <span className="text-pw-text-muted">
                          {t('manual_generate.previewPanel.cycleDays', {
                            days: rangeDays,
                          })}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                        {t('manual_generate.previewPanel.tax')}
                      </dt>
                      <dd
                        className="mt-1 font-medium text-pw-text"
                        data-testid="manual-generate-preview-tax"
                      >
                        {previewTaxLine}
                      </dd>
                    </div>
                    {notes.trim() ? (
                      <div className="sm:col-span-2">
                        <dt className="text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                          {t('manual_generate.previewPanel.internalNote')}
                        </dt>
                        <dd className="mt-1 whitespace-pre-wrap text-pw-text">
                          {notes.trim()}
                        </dd>
                      </div>
                    ) : null}
                  </dl>

                  <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                    {t('manual_generate.previewPanel.workerNotice')}
                  </div>

                  {formError ? (
                    <div
                      role="alert"
                      className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
                    >
                      {formError}
                    </div>
                  ) : null}
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-pw-border pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBackToCompose}
                    disabled={submitting}
                  >
                    {t('manual_generate.backToEdit')}
                  </Button>
                  <Button
                    type="submit"
                    data-testid="manual-generate-submit-btn"
                    disabled={submitting}
                  >
                    {submitting
                      ? t('manual_generate.generating')
                      : t('manual_generate.confirmGenerate')}
                  </Button>
                </div>
              </>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default ManualGeneratePage;
